import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	Navigate,
	useLocation,
	useNavigate,
	useParams,
} from "@tanstack/react-router"

import type { CopilotScope } from "@productlane/lib/src/ai"
import { copilotScopeKey } from "@productlane/lib/src/ai"
import { Empty } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { AgentItem } from "./agent-items"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { useShortcut } from "@/lib"
import { useIsAgentEngineAvailable } from "@/lib/hooks/use-is-agent-engine-available"
import { useIsAgentPageAvailable } from "@/lib/hooks/use-is-agent-page-available"
import { generateId } from "@/lib/id"
import { useCopilotState } from "@/lib/states/copilot-state"
import { useAuthData, useZeroWorkspace } from "@/lib/zero"
import { BreadcrumbSlash, DetailTopBar, PageHeader } from "../page-header"
import { CopilotOverlay } from "../threads/components/right-bar/ai-chat/copilot-overlay"
import { CopilotToggleButton } from "../threads/components/right-bar/ai-chat/copilot-toggle-button"
import { SidebarAiChat } from "../threads/components/right-bar/ai-chat/sidebar-ai-chat"
import { BreadcrumbBack } from "./agent-breadcrumb"
import { AGENT_COPILOT_SCOPE } from "./agent-copilot"
import { AgentHomeTodos } from "./agent-home-todos"
import { AgentListOptions, useAgentListOptions } from "./agent-list-options"
import { AgentOffState, isAgentTurnedOff } from "./agent-off-state"
import { AgentOutcomesCard } from "./agent-outcomes-card"
import {
	agentPath,
	contextScope,
	parseAgentRoute,
	parseExpandedScope,
} from "./agent-route"
import { isMissingItem, stepTarget } from "./agent-selection"
import { AssistantChatsPage } from "./assistant-chats-page"
import { BatchReview } from "./batch-review"
import { ChatPageActions } from "./chat-page-actions"
import { useLoadChatSession } from "./chat-session-loader"
import { CodeFixMessage } from "./code-fix-detail"
import { DecidedList, DecidedSection, LinkedList } from "./decided-list"
import { DecisionBarProvider } from "./decision-bar"
import {
	DocsSuggestionMessage,
	ProposalSuggestionMessage,
} from "./docs-suggestion-message"
import {
	ExpandedChatContext,
	useChangelogPageContext,
} from "./expanded-chat-context"
import { AGENT_EMPTY_STATES, matchesTab } from "./format"
import { ideaTitle } from "./idea-list"
import { IdeaMessage } from "./idea-message"
import { CHART_WINDOW_DAYS, forecastRange } from "./resolution-forecast-model"
import { ResolutionsByChannelChart } from "./resolutions-by-channel-chart"
import { EmptyRecommendations, useAgentReview } from "./review-state"
import { SetupMessage } from "./setup-message"
import { useAgentItems } from "./use-agent-recommendations"
import { useReadIdeas } from "./use-read-ideas"

// The item stays open while its id is in the URL, so accepting an idea (which
// takes it off the list) does not pull the pane to the next one.
function useSelectedItem(
	visible: readonly AgentItem[],
	itemId: string | null,
): AgentItem | null {
	const lastRef = useRef<AgentItem | null>(null)
	const found = itemId ? visible.find((item) => item.id === itemId) : undefined
	const selected =
		found ?? (itemId && lastRef.current?.id === itemId ? lastRef.current : null)
	lastRef.current = selected
	return selected
}

export const AgentScreen = memo(() => {
	const available = useIsAgentPageAvailable()
	const engine = useIsAgentEngineAvailable()
	const workspace = useZeroWorkspace()
	const { userId } = useAuthData()
	const navigate = useNavigate()
	const params = useParams({ strict: false })
	const route = parseAgentRoute(params)
	const { tab, itemId } = route
	// "+" in the sidebar: a fresh chat with the agent, new on every click.
	const locationKey = useLocation({ select: (l) => l.state.__TSR_key })
	const locationSearch = useLocation({ select: (l) => l.searchStr })
	// The history key survives a reload, so it marks a navigation but cannot name a chat.
	// eslint-disable-next-line react-hooks/exhaustive-deps -- a new id per navigation
	const newChatId = useMemo(() => generateId(), [locationKey])
	// A chat that is not an idea: a fresh one from "+", or an assistant panel expanded here.
	const chatScope = useMemo<CopilotScope | null>(() => {
		if (params.tab === "new") return { kind: "agent", chatId: newChatId }
		if (params.tab === "chat") return parseExpandedScope(locationSearch)
		return null
	}, [params.tab, newChatId, locationSearch])
	const newChat = chatScope !== null
	// Expanded from a changelog, article, or thread: that page stays open on the right.
	const sourceScope = params.tab === "chat" ? contextScope(chatScope) : null
	const changelogContext = useChangelogPageContext(sourceScope)
	const { items, loaded } = useAgentItems()
	const review = useAgentReview()
	const { markRead } = useReadIdeas(userId)
	const [decisionSlot, setDecisionSlot] = useState<HTMLDivElement | null>(null)
	const [peekSlot, setPeekSlot] = useState<HTMLDivElement | null>(null)
	const [chartRange] = useState(() =>
		forecastRange(new Date(), CHART_WINDOW_DAYS),
	)
	const visible = useMemo(
		() => items.filter((item) => matchesTab(item.type, tab)),
		[items, tab],
	)
	const { showAccepted, showDeclined, showLinked } = useAgentListOptions()
	// A decided card opened from the Accepted or Declined tab is no longer in the open list.
	const openHasItem = !itemId || visible.some((item) => item.id === itemId)
	const [decidedCard, decidedCardResult] = useQuery(
		queries.agentRecommendation({ id: itemId ?? "" }),
		{ enabled: !openHasItem },
	)
	const selectable = useMemo(
		() =>
			!openHasItem && decidedCard
				? [
						...visible,
						{
							kind: "recommendation" as const,
							id: decidedCard.id,
							type: decidedCard.type,
							recommendation: decidedCard,
						},
					]
				: visible,
		[openHasItem, decidedCard, visible],
	)
	const picked = useSelectedItem(selectable, itemId)
	const selected = newChat ? null : picked
	const index = selected ? visible.findIndex((i) => i.id === selected.id) : -1
	const cursorId = selected?.id ?? null
	const selectedTitle = selected
		? selected.kind === "recommendation"
			? selected.recommendation.title
			: ideaTitle(selected)
		: undefined

	useEffect(() => {
		if (cursorId) markRead(cursorId)
	}, [cursorId, markRead])

	const recommendationContextId =
		selected?.kind === "recommendation" ? selected.id : undefined
	// Read on every turn, so the assistant edits whichever idea is open now.
	const getPageContext = useCallback(
		() =>
			recommendationContextId
				? { recommendationId: recommendationContextId }
				: undefined,
		[recommendationContextId],
	)
	// One assistant chat per idea, so switching ideas never shows another's replies.
	// Docs suggestions and code fixes have no idea row, so their chat keys on the item.
	const chatItemId =
		selected?.kind === "docs" ||
		selected?.kind === "code" ||
		selected?.kind === "proposal" ||
		selected?.kind === "setup"
			? selected.id
			: undefined
	const ideaScope = useMemo<CopilotScope | null>(() => {
		if (recommendationContextId)
			return { kind: "agent", recommendationId: recommendationContextId }
		if (chatItemId) return { kind: "agent", chatId: chatItemId }
		return null
	}, [recommendationContextId, chatItemId])

	const goTo = useCallback(
		(item: AgentItem | undefined) =>
			void navigate({ href: agentPath(tab, item?.id), replace: true }),
		[navigate, tab],
	)
	// Like every other list: the arrows and J/K step through it. A chat keeps them for its composer.
	const step = (direction: 1 | -1) => {
		const id = stepTarget(
			visible.map((item) => item.id),
			cursorId,
			direction,
		)
		if (id) void navigate({ href: agentPath(tab, id), replace: true })
	}
	useShortcut("ArrowDown, j", () => step(1), {
		preventDefault: true,
		enabled: !newChat && !!itemId,
	})
	useShortcut("ArrowUp, k", () => step(-1), {
		preventDefault: true,
		enabled: !newChat && !!itemId,
	})

	const onClosed = useCallback(() => {
		goTo(visible[index + 1] ?? visible[index - 1])
	}, [goTo, visible, index])

	// A new or expanded chat takes the keyboard in its composer, like Claude. An idea
	// keeps it on the page, where Esc declines and Cmd+Enter accepts.
	const focusScope = chatScope
	const focusKey = focusScope ? copilotScopeKey(focusScope) : null
	useEffect(() => {
		if (focusScope) useCopilotState.getState().requestFocus(focusScope)
		// eslint-disable-next-line react-hooks/exhaustive-deps -- once per opened chat
	}, [focusKey])

	// A stored chat opened from the sidebar or View all carries its session.
	useLoadChatSession(
		params.tab === "chat"
			? new URLSearchParams(locationSearch).get("session")
			: null,
	)

	if (route.redirect) return <Navigate to={route.redirect} replace />
	if (available === false) return <Navigate to="/threads" replace />
	if (available === undefined) return null

	if (isAgentTurnedOff(workspace?.aiChatSettings)) {
		return (
			<RoleCheck not={["VIEWER"]}>
				<MetaInfo title="Agent" />
				<AgentOffState />
			</RoleCheck>
		)
	}

	if (params.tab === "chats" && !route.redirect) {
		return (
			<RoleCheck not={["VIEWER"]}>
				<MetaInfo title="All chats" />
				<AssistantChatsPage />
			</RoleCheck>
		)
	}

	const writing = engine && review.running
	const notFound =
		!newChat &&
		!selected &&
		isMissingItem({
			itemId,
			inOpenList: openHasItem,
			listLoaded: loaded,
			closedLookup: decidedCard
				? "found"
				: decidedCardResult.type === "complete"
					? "none"
					: "loading",
		})
	const showEngineEmpty =
		engine && (tab === "all" || tab === "guidance") && visible.length === 0
	const empty =
		loaded && visible.length === 0 && !writing ? (
			showEngineEmpty ? (
				<EmptyRecommendations review={review} className="" />
			) : (
				<Empty
					title={AGENT_EMPTY_STATES[tab].title}
					text={AGENT_EMPTY_STATES[tab].text}
				/>
			)
		) : null

	const decided = (compact: boolean) => (
		<>
			{showAccepted && (
				<DecidedSection label="Accepted">
					<DecidedList tab="accepted" selectedId={cursorId} compact={compact} />
				</DecidedSection>
			)}
			{showDeclined && (
				<DecidedSection label="Declined">
					<DecidedList tab="declined" selectedId={cursorId} compact={compact} />
				</DecidedSection>
			)}
			{showLinked && (
				<DecidedSection label="Linear issues">
					<LinkedList compact={compact} />
				</DecidedSection>
			)}
		</>
	)

	// Nothing open: the list in the middle, the assistant beside it.
	if (!newChat && !itemId) {
		return (
			<RoleCheck not={["VIEWER"]}>
				<MetaInfo title="Agent" />
				<div className="relative flex min-h-0 flex-1">
					<div className="flex min-w-0 flex-1 flex-col">
						<PageHeader
							title="Agent"
							clearsNavToggle
							actions={
								<div className="flex items-center gap-1">
									<AgentListOptions />
									{/* Stays put while open, pressed, so the same click closes it. */}
									<CopilotToggleButton scope={AGENT_COPILOT_SCOPE} />
								</div>
							}
						/>
						<div className="min-h-0 flex-1 overflow-y-auto">
							<div
								className={`mx-auto flex w-full max-w-[720px] flex-col gap-6 px-4 pt-6 ${empty ? "min-h-full justify-center pb-16" : "pb-10"}`}
							>
								{/* The rate and its history first, then the changes that raise it. */}
								<div className="grid gap-4 sm:grid-cols-2">
									<AgentOutcomesCard />
									<ResolutionsByChannelChart
										query={chartRange}
										height={120}
										showRate={false}
										className="bg-white dark:bg-gray-900"
									/>
								</div>
								{empty ?? <BatchReview items={visible} peekSlot={peekSlot} />}
								{decided(false)}
							</div>
						</div>
					</div>
					<CopilotOverlay layout="push" scope={AGENT_COPILOT_SCOPE} />
					{/* Where the Space preview floats, at the top right of the page. */}
					<div
						ref={setPeekSlot}
						data-peek-slot
						className="pointer-events-none absolute right-4 top-16 z-30 w-[22rem]"
					/>
				</div>
			</RoleCheck>
		)
	}

	return (
		<RoleCheck not={["VIEWER"]}>
			<MetaInfo title={newChat ? "Assistant" : "Agent"} />
			<div className="flex min-h-0 flex-1">
				<section className="flex min-w-0 flex-1 flex-col">
					{chatScope && (
						<div
							data-testid="agent-new-chat"
							className="flex min-h-0 flex-1 flex-col bg-gray-50 dark:bg-gray-850"
						>
							<DetailTopBar
								className="justify-between pl-7 pr-4"
								clearsNavToggle
							>
								<nav
									aria-label="Breadcrumb"
									className="flex min-w-0 items-center gap-2 text-sm"
								>
									<BreadcrumbBack
										onClick={() => void navigate({ href: agentPath(tab) })}
									/>
									<BreadcrumbSlash />
									<span className="min-w-0 truncate text-primary">
										{params.tab === "new" ? "New chat" : "Assistant"}
									</span>
								</nav>
								<ChatPageActions scope={chatScope} />
							</DetailTopBar>
							<div className="flex min-h-0 w-full flex-1">
								<div className="flex min-h-0 min-w-0 flex-1 flex-col">
									<SidebarAiChat
										key={copilotScopeKey(chatScope)}
										scope={chatScope}
										getPageContext={
											sourceScope?.kind === "changelog"
												? changelogContext.getPageContext
												: undefined
										}
										embedded
										emptyActions={
											params.tab === "new" ? <AgentHomeTodos /> : undefined
										}
									/>
								</div>
								{sourceScope && (
									<ExpandedChatContext
										scope={sourceScope}
										registerEditorGetter={changelogContext.register}
									/>
								)}
							</div>
						</div>
					)}
					{notFound && (
						<div
							data-testid="agent-item-not-found"
							className="flex min-h-0 flex-1 flex-col bg-gray-50 dark:bg-gray-850"
						>
							<DetailTopBar className="pl-7 pr-4" clearsNavToggle>
								<nav aria-label="Breadcrumb" className="flex text-sm">
									<BreadcrumbBack
										onClick={() => void navigate({ href: agentPath(tab) })}
									/>
								</nav>
							</DetailTopBar>
							<div className="flex flex-1 items-center justify-center p-8 pb-16">
								<Empty
									title="This suggestion is closed"
									text="It was accepted, declined or removed. Pick another from the list."
								/>
							</div>
						</div>
					)}
					{loaded && showEngineEmpty && !selected && !newChat && (
						<div
							data-testid="agent-review-pane"
							className="flex flex-1 items-center justify-center p-8 pb-16 max-md:hidden"
						>
							<EmptyRecommendations review={review} className="" />
						</div>
					)}
					{selected && ideaScope && (
						<div
							data-testid="agent-idea-chat"
							// The assistant panel's gray, so the idea card stands out on it.
							className="flex min-h-0 flex-1 flex-col bg-gray-50 dark:bg-gray-850"
						>
							<DetailTopBar
								className="justify-between pl-7 pr-4"
								clearsNavToggle
							>
								{/* The list is the overview now, so the way back is the crumb. */}
								<nav
									aria-label="Breadcrumb"
									className="flex min-w-0 items-center gap-2 text-sm"
								>
									<BreadcrumbBack
										onClick={() => void navigate({ href: agentPath(tab) })}
									/>
									<BreadcrumbSlash />
									<span className="min-w-0 truncate text-primary">
										{selectedTitle}
									</span>
								</nav>
								<div className="flex flex-none items-center gap-2">
									<div
										ref={setDecisionSlot}
										data-testid="agent-decision-bar"
										className="flex items-center gap-2"
									/>
									<ChatPageActions scope={ideaScope} collapsible={false} />
								</div>
							</DetailTopBar>
							<DecisionBarProvider value={decisionSlot}>
								<div className="flex min-h-0 w-full flex-1 flex-col">
									<SidebarAiChat
										key={selected.id}
										scope={ideaScope}
										getPageContext={getPageContext}
										embedded
										// The agent's message is what you came to read, so it opens there.
										startAtTop
										leading={
											selected.kind === "recommendation" ? (
												<IdeaMessage
													recommendation={selected.recommendation}
													onClosed={onClosed}
												/>
											) : selected.kind === "docs" ? (
												<DocsSuggestionMessage
													group={selected.group}
													onClosed={onClosed}
												/>
											) : selected.kind === "setup" ? (
												<SetupMessage item={selected} onClosed={onClosed} />
											) : selected.kind === "proposal" ? (
												<ProposalSuggestionMessage
													row={selected.row}
													onClosed={onClosed}
												/>
											) : (
												<CodeFixMessage item={selected} onClosed={onClosed} />
											)
										}
									/>
								</div>
							</DecisionBarProvider>
						</div>
					)}
				</section>
			</div>
		</RoleCheck>
	)
})
